import type { ComponentPropsWithoutRef } from "react";
import { twMerge } from "tailwind-merge";
import { MarbleJoystick } from "./marble-joystick";
import type { ControlMode, StickVector } from "./control-types";
import { ButtonSimple } from "../RtfmDemoApp";
import { ImageIcon, ReloadIcon, ShuffleIcon, StopwatchIcon } from "@radix-ui/react-icons";
import { SceneLoaderHook } from "@/hooks/useSceneLoader";

type StickConfig = {
  id: string;
  label: string;
  value: StickVector;
  onChange: (vector: StickVector) => void;
  disabled?: boolean;
  /**
   * External input such as keyboard should still update the visual even when the stick is idle.
   */
  displayValue?: StickVector;
  onStart?: () => void;
  onEnd?: () => void;
};

type DualStickGamepadProps = {
  left: StickConfig;
  right: StickConfig;
  mode: ControlMode;
  onModeChange: (mode: ControlMode) => void;
} & ComponentPropsWithoutRef<"div">;

const MODE_OPTIONS: Array<{ label: string; value: ControlMode; description: string }> = [
  { label: "FPS", value: "fps", description: "Grounded + gravity" },
  { label: "Flight", value: "flight", description: "Six degrees of freedom" },
];

export const DualStickGamepad = ({
  left,
  right,
  mode,
  onModeChange,
  className,
  sceneLoader,
  ...rest
}: DualStickGamepadProps & { sceneLoader: SceneLoaderHook }) => {
  return (
    <div
      className={twMerge(
        "h-full w-full pointer-events-auto mx-auto flex flex-col gap-4 text-white",
        className
      )}
      {...rest}
    >
      <div className="w-full h-full p-0 flex flex-row gap-2 items-center justify-center">
        <StickPanel
          config={left}
          className="border border-primary rounded-[1em] p-1"
        />
        <div className="grow max-h-full h-full flex flex-row justify-center gap-1 text-[0.8em] items-center">
          <div className="w-[10em] flex flex-col items-center opacity-75 justify-center gap-1">
            <ButtonSimple
              onClick={sceneLoader.resetSceneRequest}
              className="flex flex-row w-full border-none bg-secondary px-3 py-1.5 items-center justify-center shrink-1 rounded-md"
              tabIndex={0}
            >
              <ReloadIcon className="text-primary w-3 h-3" />
              <p className={'w-3/4 text-center whitespace-pre-wrap-none'}>recenter</p>
            </ButtonSimple>
            {/* <ButtonSimple
            onClick={sceneLoader.randomSceneRequest}
            className="flex flex-row w-full border-none bg-secondary px-3 py-1.5 items-center justify-center shrink-1 rounded-md"
            tabIndex={0}
            >
              <ShuffleIcon className="text-primary w-3 h-3" />
              <p className={'w-3/4 text-center whitespace-pre-wrap-none'}>random</p>
            </ButtonSimple> */}
            <ButtonSimple
              onClick={sceneLoader.toggleNumSteps}
              className="flex flex-row w-full border-none bg-secondary px-3 py-1.5 items-center justify-center shrink-1 rounded-md"
              tabIndex={0}
            >
              {sceneLoader.numSteps === 5 ? <ImageIcon className="text-primary w-3 h-3" /> : <StopwatchIcon className="text-primary w-3 h-3" />}
              <p className={'w-3/4 text-center whitespace-pre-wrap-none'}>{sceneLoader.numSteps === 5 ? "quality" : "speed"}</p>
            </ButtonSimple>
          </div>
        </div>
        <StickPanel
          config={right}
          className="border border-primary rounded-[1em] p-1"
        />
      </div>
      {/* <ModeToggle mode={mode} onModeChange={onModeChange} /> */}
    </div>
  );
};

const StickPanel = ({
  config,
  className,
}: {
  config: StickConfig;
  className?: string;
}) => {
  return (
    <div className={twMerge("aspect-square h-full w-auto flex flex-col items-center justify-center", className)}>
      <MarbleJoystick
        aria-label={`Joystick`}
        className="h-full w-full rounded-full"
        onMove={(data) => {
          if (!config.disabled) {
            config.onChange({ x: data.x, y: data.y });
          }
        }}
        onStart={() => {
          if (!config.disabled) {
            config.onStart?.();
          }
        }}
        onEnd={() => {
          config.onEnd?.();
        }}
        value={config.displayValue ?? config.value}
      />
    </div>
  );
};

const ModeToggle = ({
  mode,
  onModeChange,
}: {
  mode: ControlMode;
  onModeChange: (mode: ControlMode) => void;
}) => {
  return (
    <div className="flex flex-col items-center gap-3">
      <span className="text-sm font-semibold uppercase tracking-wide text-white/80">Mode</span>
      <div className="flex flex-col gap-2 rounded-2xl bg-black/40 p-3 shadow-inner">
        {MODE_OPTIONS.map((option) => {
          const isActive = option.value === mode;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onModeChange(option.value)}
              className={twMerge(
                "flex w-48 flex-col items-center justify-center rounded-xl border px-3 py-2 text-center transition-colors",
                isActive
                  ? "border-emerald-300/80 bg-emerald-400/20 text-emerald-100"
                  : "border-white/10 bg-transparent text-white/70 hover:border-white/30 hover:text-white"
              )}
            >
              <span className="text-sm font-semibold uppercase tracking-[0.2em]">{option.label}</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                {option.description}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
